import React, { useState, useEffect } from 'react';
import { getFridges, createFridge, getApartments } from '../../services/api';
import { QrCodeIcon, PlusIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const FridgeManagement = () => {
  const [fridges, setFridges] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    apartment_id: '',
    location: ''
  });

  useEffect(() => {
    loadFridges();
    loadApartments();
  }, []);

  const loadFridges = async () => {
    try {
      const response = await getFridges();
      // Flask returns {success: true, fridges: [...]}
      setFridges(response.data.fridges || []);
    } catch (error) {
      console.error('Error loading fridges:', error);
      toast.error('Failed to load fridges');
    } finally {
      setLoading(false);
    }
  };

  const loadApartments = async () => {
    try {
      const response = await getApartments();
      // Flask returns {success: true, apartments: [...]}
      setApartments(response.data.apartments || []);
    } catch (error) {
      console.error('Error loading apartments:', error);
      toast.error('Failed to load apartments');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.apartment_id) {
      toast.error('Please select an apartment');
      return;
    }

    try {
      const response = await createFridge(formData);
      
      if (response.data.success) {
        toast.success('Fridge created successfully');
        setShowModal(false);
        setFormData({ apartment_id: '', location: '' });
        loadFridges();
        
        // Download QR code
        if (response.data.fridge?.qr_path) {
          window.open(`http://localhost:5000/${response.data.fridge.qr_path}`, '_blank');
        }
      } else {
        toast.error(response.data.message || 'Failed to create fridge');
      }
    } catch (error) {
      console.error('Error creating fridge:', error);
      toast.error(error.response?.data?.message || 'Failed to create fridge');
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const downloadQR = (qrPath, fridgeId) => {
    if (qrPath) {
      window.open(`http://localhost:5000/${qrPath}`, '_blank');
      toast.success('QR Code downloaded');
    } else {
      toast.error('QR Code not available');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'active': { bg: 'bg-green-100', text: 'text-green-800', label: 'Active' },
      'inactive': { bg: 'bg-red-100', text: 'text-red-800', label: 'Inactive' },
      'maintenance': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Maintenance' }
    };
    
    const statusConfig = statusMap[status] || statusMap['active'];
    
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${statusConfig.bg} ${statusConfig.text}`}>
        {statusConfig.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Fridge Management</h1>
          <button
            onClick={() => setShowModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-blue-700 transition duration-200"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Fridge
          </button>
        </div>

        {fridges.length === 0 ? (
          <div className="bg-white rounded-lg shadow-lg p-12 text-center">
            <QrCodeIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Fridges Yet</h3>
            <p className="text-gray-500 mb-6">Get started by adding your first fridge</p>
            <button
              onClick={() => setShowModal(true)}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
            >
              Add Your First Fridge
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {fridges.map((fridge) => (
              <div key={fridge.id} className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition duration-200">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">
                      {fridge.apartment_name || 'Unknown Apartment'}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      ID: {fridge.fridge_id}
                    </p>
                    <p className="text-gray-600 text-sm">
                      Location: {fridge.location || 'Not specified'}
                    </p>
                  </div>
                  {getStatusBadge(fridge.status)}
                </div>
                
                <div className="mb-4 text-sm text-gray-500">
                  <p>Created: {new Date(fridge.created_at).toLocaleDateString()}</p>
                </div>

                <div className="flex space-x-2">
                  <button
                    onClick={() => downloadQR(fridge.qr_code, fridge.fridge_id)}
                    className="flex-1 bg-purple-50 text-purple-600 py-2 px-4 rounded-lg flex items-center justify-center hover:bg-purple-100 transition duration-200"
                  >
                    <QrCodeIcon className="h-5 w-5 mr-2" />
                    Download QR
                  </button>
                  
                  <button
                    onClick={() => window.open(`/fridge-store/${fridge.fridge_id}`, '_blank')}
                    className="flex-1 bg-blue-50 text-blue-600 py-2 px-4 rounded-lg hover:bg-blue-100 transition duration-200"
                  >
                    View Store
                  </button>
                </div>

                {/* Admin Actions */}
                <div className="mt-4 pt-4 border-t border-gray-200 flex justify-end space-x-2">
                  <button
                    onClick={() => {/* Navigate to edit */}}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => {/* Handle delete */}}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add Fridge Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-lg bg-white">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Add New Fridge</h3>
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Select Apartment *
                  </label>
                  <select
                    name="apartment_id"
                    value={formData.apartment_id}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                    required
                  >
                    <option value="">Choose an apartment</option>
                    {apartments.map((apt) => (
                      <option key={apt.id} value={apt.id}>
                        {apt.name}
                      </option>
                    ))}
                  </select>
                  {apartments.length === 0 && (
                    <p className="text-xs text-red-500 mt-1">
                      No apartments found. Please create an apartment first.
                    </p>
                  )}
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Location/Floor *
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder="e.g., Ground Floor, Block A"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                    required
                  />
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 transition duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-200"
                    disabled={apartments.length === 0}
                  >
                    Create Fridge
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FridgeManagement;